import { useTheme } from '../../../hooks/useTheme';
import { industrySectors } from '../../../mocks/industries';

const SECTOR_ICONS: Record<string, string> = {};
industrySectors.forEach((s) => { SECTOR_ICONS[s.id] = s.icon; });

interface IndustriesHeroProps {
  activeSector: string | null;
  onSectorClick: (id: string) => void;
}

export default function IndustriesHero({ activeSector, onSectorClick }: IndustriesHeroProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <section
      className="relative pt-32 pb-16 px-4 sm:px-6 overflow-hidden"
      style={{
        background: isLight
          ? 'linear-gradient(160deg, #f0f9ff 0%, #ffffff 50%, #f0fdf4 100%)'
          : 'linear-gradient(160deg, #060c1c 0%, #0a1628 50%, #060c1c 100%)',
      }}
    >
      {/* Decorative blobs */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,207,255,0.07) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10 text-center">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase mb-6 font-body"
          style={{ borderColor: '#00cfff35', backgroundColor: '#00cfff0d', color: '#00cfff' }}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-building-2-line text-xs" />
          </div>
          Sectores & Industrias
        </div>

        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-5 leading-tight"
          style={{ color: isLight ? '#0a1628' : '#ffffff' }}
        >
          Software de Gestión y{' '}
          <span className="text-electric">Facturación e-CF</span>
          <br />
          <span style={{ color: isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.45)' }}>
            para Cada Tipo de Negocio
          </span>
        </h1>

        <p
          className="font-body text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10"
          style={{ color: isLight ? 'rgba(10,22,40,0.60)' : 'rgba(255,255,255,0.55)' }}
        >
          Conoce cómo InnoFlex se adapta a la operación específica de tu industria. Sistema POS, ERP, contabilidad y certificación e-CF certificada ante la DGII.
        </p>

        {/* Sector pills */}
        <div className="flex flex-wrap justify-center gap-3">
          {industrySectors.map((sector) => {
            const isActive = activeSector === sector.id;
            return (
              <button
                key={sector.id}
                onClick={() => onSectorClick(sector.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: isActive ? sector.accentColor + '20' : isLight ? 'rgba(10,22,40,0.04)' : 'rgba(255,255,255,0.05)',
                  borderColor: isActive ? sector.accentColor + '60' : isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.12)',
                  color: isActive ? sector.accentColor : isLight ? 'rgba(10,22,40,0.65)' : 'rgba(255,255,255,0.60)',
                  transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
                }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${sector.icon} text-xs`} />
                </div>
                {sector.title}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
